// Admin conversations store backing the RealtimeConversationsProvider
// (`./RealtimeConversationsProvider.tsx`). BL-1489.
//
// The conversations-shaped BINDING of the generic engine in
// `./entity-state-store.ts` — the fourth sibling after `lot-state-store.ts`,
// `registration-state-store.ts` and `max-bid-state-store.ts`. It supplies the
// per-stream details (`customerUserId` as the fold key, the wall-clock `cursor`
// ordering key, the `conversation.delta` event, and the catch-up response's
// `conversations` collection); every line of fold / cursor / cold-seed /
// reconcile / connection logic is inherited.
//
// UNSEQUENCED, ON PURPOSE. The other three bindings turned gap detection on by
// reporting their catch-up position under `sequence` (BL-1628 / BL-1630). This
// one reports `cursor` — `updated_at` as epoch ms — because the 2026-09-04
// amendment (D15) withdrew the snapshot table a dense per-tenant counter would
// need, and because nothing renders from this stream: it is a TRIGGER for an
// authenticated re-read, so a dropped frame costs one reconcile poll, not data.
// Do not "upgrade" it to a sequence without first deciding where that sequence
// would live (rule #24 bans a new table-side mechanism for it).

import type { AdminConversationDelta } from '../../mappers/realtime/admin-conversations';
import type { RealtimeSubscriber } from '../../types/realtime/subscriber';
import {
    EntityStateStore,
    DISCONNECTED_ENTITY_STATE,
    type EntityStateConnection,
    type NotifyScheduler,
} from './entity-state-store';

/**
 * Result of the admin conversations catch-up route
 * (`createConversationsCatchupHandler`): the minted obscured `topic`, every
 * thread changed since the client's position, and the max `cursor` in that set
 * (the client's next `since_cursor`).
 *
 * Reports `cursor`, NOT `sequence` — this stream is unsequenced (see header).
 * There is no cap and no `hasMore` on this payload (AC 11): the change set is
 * identifiers only, so its size is the number of threads touched, never a
 * window into the inbox.
 *
 * This declaration is the ONE the wire envelope uses (ADR 027 — Response
 * Contract): `ConversationsCatchupResponseBody` in `@auctogon/realtime` imports
 * it rather than re-declaring the shape.
 */
export interface ConversationStateCatchupResult {
    topic: string;
    conversations: AdminConversationDelta[];
    cursor: number;
}

/**
 * Fetcher seam: given the tenant + the client's current position, hit the
 * authenticated admin conversations catch-up route. `sinceCursor === 0` is the
 * cold-seed — position only: no rows, just the tenant's newest cursor, since
 * nothing renders from this stream and the trigger ignores its first
 * observation. The app injects this via the provider.
 */
export type ConversationStateCatchupFetcher = (
    tenantId: string,
    sinceCursor: number,
) => Promise<ConversationStateCatchupResult>;

/** The conversations stream's binding of the engine's per-stream details. */
const CONVERSATION_STATE_CONFIG = {
    keyOf: (delta: AdminConversationDelta) => delta.customerUserId,
    cursorOf: (delta: AdminConversationDelta) => delta.cursor,
    event: 'conversation.delta' as const,
    itemsOf: (result: ConversationStateCatchupResult) => result.conversations,
    // One delta per frame: the batch publisher already collapses a transaction
    // to one frame PER THREAD, so a frame never carries more than one.
    deltasOf: (payload: unknown) => [(payload as { state: AdminConversationDelta }).state],
};

/** Whether the conversations stream is established. */
export type ConversationStateConnection = EntityStateConnection;

/** Stable pre-connection value (ref-stable for `useSyncExternalStore`). */
export const DISCONNECTED_CONVERSATION_STATE: ConversationStateConnection =
    DISCONNECTED_ENTITY_STATE;

/**
 * The admin conversations store for ONE tenant. A thin conversations-shaped
 * facade over `EntityStateStore` — `getConversations()` is the domain name for
 * the engine's `getEntities()`; everything else is inherited unchanged.
 */
export class ConversationStateStore extends EntityStateStore<
    AdminConversationDelta,
    ConversationStateCatchupResult
> {
    constructor(
        subscriber: RealtimeSubscriber,
        fetcher: ConversationStateCatchupFetcher,
        tenantId: string,
        scheduleNotify?: NotifyScheduler,
    ) {
        super(subscriber, fetcher, tenantId, CONVERSATION_STATE_CONFIG, scheduleNotify);
    }

    /** The current folded thread map. Stable ref until a fold changes it. */
    getConversations(): ReadonlyMap<string, AdminConversationDelta> {
        return this.getEntities();
    }
}
